import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Text } from './Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from './icons';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { t } from '../i18n';
import { FEATURE_ACCENTS, type FeatureKey } from '../theme/featureAccents';
import { colors, screenPadding } from '../theme/tokens';

/**
 * Orbite de l'écran d'accueil avant connexion : six fonctionnalités qui tournent autour de
 * « Ton contrat ».
 *
 * TOUTE LA GÉOMÉTRIE VIT DANS UNE BOÎTE DE 320 PT, celle de la maquette. Sur un écran plus étroit on
 * ne recalcule rien : la boîte entière est réduite d'un bloc (`transform: scale`), proportions
 * gardées, et sa place dans la mise en page est réduite d'autant. C'est ce qui évite le défaut de
 * l'ancien schéma, dont chaque cote dépendait de la largeur et dont les cartes finissaient par se
 * recouvrir.
 *
 * MOUVEMENT. Le calque des puces fait un tour en 48 s ; chaque puce tourne en sens inverse du même
 * angle et reste donc DROITE. Le pointillé extérieur tourne lentement à rebours (90 s). Animations
 * réduites : rien ne bouge, les puces restent à leur angle de départ.
 *
 * Le schéma décrit, il ne navigue pas : aucune puce n'est un bouton.
 *
 * Les COULEURS des icônes viennent de FEATURE_ACCENTS, la table partagée avec l'accueil connecté et
 * Carrière : une fonctionnalité garde son accent d'un écran à l'autre (voir featureAccents.ts).
 */

const BOITE = 320;
const PUCE = 72;
const RAYON_ORBITE = 126;
// Coin haut-gauche d'une puce posée au centre de la boîte : (320 - 72) / 2.
const ORIGINE = (BOITE - PUCE) / 2;
const TOUR_ORBITE = 48_000;
const TOUR_POINTILLE = 90_000;

/**
 * Taille de l'orbite : 320 pt au plus, la place disponible en largeur, et un plafond éventuel donné
 * par l'écran qui la porte (ce qu'il lui reste de hauteur).
 */
export function orbiteTaille(
  largeurDisponible: number,
  plafond: number = Number.POSITIVE_INFINITY,
): { taille: number; echelle: number } {
  const taille = Math.max(0, Math.min(BOITE, largeurDisponible, plafond));
  return { taille, echelle: taille / BOITE };
}

interface Puce {
  key: FeatureKey;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  /** Angle sur l'orbite, 0° = à droite, sens horaire : le CV en haut. */
  angle: number;
}

const PUCES: Puce[] = [
  { key: 'cv', icon: 'file-document-outline', angle: -90 },
  { key: 'linkedin', icon: 'linkedin', angle: -30 },
  { key: 'interview', icon: 'chat', angle: 30 },
  { key: 'coach', icon: 'account-star', angle: 90 },
  { key: 'offers', icon: 'target', angle: 150 },
  { key: 'letter', icon: 'email-edit-outline', angle: 210 },
];

export interface FeatureWheelProps {
  testID?: string;
  // Plafond de taille, en points. L'écran d'accueil le déduit de la hauteur de l'appareil.
  tailleMax?: number;
}

export function FeatureWheel({ testID = 'feature-wheel', tailleMax }: FeatureWheelProps) {
  // Largeur de fenêtre plutôt qu'onLayout : disponible dès le premier rendu, donc pas de schéma qui
  // saute d'une taille à l'autre. Sans plafond fourni, on retire `screenPadding` de chaque côté ;
  // l'écran d'accueil, dont les marges sont plus larges, passe `tailleMax`.
  const { width } = useWindowDimensions();
  const { taille, echelle } = orbiteTaille(width - 2 * screenPadding, tailleMax);

  const reduit = useReduceMotion();
  const orbite = useRef(new Animated.Value(0)).current;
  const pointille = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reduit) return undefined;
    orbite.setValue(0);
    pointille.setValue(0);
    const boucle = (valeur: Animated.Value, duree: number) =>
      Animated.loop(
        Animated.timing(valeur, { toValue: 1, duration: duree, easing: Easing.linear, useNativeDriver: true }),
      );
    const boucles = [boucle(orbite, TOUR_ORBITE), boucle(pointille, TOUR_POINTILLE)];
    boucles.forEach((b) => b.start());
    return () => boucles.forEach((b) => b.stop());
  }, [reduit, orbite, pointille]);

  const tourne = reduit ? undefined : orbite.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  const contreTour = reduit ? undefined : orbite.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-360deg'] });
  const tourneArriere = reduit
    ? undefined
    : pointille.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-360deg'] });

  return (
    <View testID={testID} style={[styles.enveloppe, { width: taille, height: taille }]}>
      <View
        style={[
          styles.boite,
          { left: (taille - BOITE) / 2, top: (taille - BOITE) / 2, transform: [{ scale: echelle }] },
        ]}
      >
        {/* Anneaux décoratifs : hors de l'arbre d'accessibilité et intouchables. */}
        <View
          testID={`${testID}-anneaux`}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <Animated.View style={[styles.pointille, tourneArriere ? { transform: [{ rotate: tourneArriere }] } : null]} />
          <View style={styles.anneauInterieur} />
        </View>

        <Animated.View style={[StyleSheet.absoluteFill, tourne ? { transform: [{ rotate: tourne }] } : null]}>
          {PUCES.map(({ key, icon, angle }) => (
            <View
              key={key}
              testID={`${testID}-${key}`}
              accessible
              accessibilityLabel={t(`onboarding.wheel.${key}`)}
              style={[
                styles.emplacement,
                // Éloignée de l'orbite, puis contre-tournée : la puce est droite à l'arrêt.
                { transform: [{ rotate: `${angle}deg` }, { translateX: RAYON_ORBITE }, { rotate: `${-angle}deg` }] },
              ]}
            >
              <Animated.View
                testID={`${testID}-${key}-puce`}
                style={[styles.puce, contreTour ? { transform: [{ rotate: contreTour }] } : null]}
              >
                <MaterialCommunityIcons
                  testID={`${testID}-${key}-icon`}
                  name={icon}
                  size={22}
                  color={FEATURE_ACCENTS[key].color}
                />
                {/* Deux lignes au plus, réduites au besoin : une puce qui grandit casserait l'orbite. */}
                <Text style={styles.libelle} numberOfLines={2} adjustsFontSizeToFit minimumFontScale={0.8}>
                  {t(`onboarding.wheel.${key}`)}
                </Text>
              </Animated.View>
            </View>
          ))}
        </Animated.View>

        {/* Cœur : contour dégradé de 3 pt, disque bleu nuit au-dedans. */}
        <LinearGradient
          testID={`${testID}-center`}
          colors={[colors.cyan, colors.electric, colors.violetLogo, colors.corail]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.coeur}
        >
          <View style={styles.coeurInterieur}>
            <MaterialCommunityIcons name="file-check-outline" size={34} color={colors.white} />
            <Text style={styles.coeurLibelle} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
              {t('onboarding.wheel.center')}
            </Text>
          </View>
        </LinearGradient>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  enveloppe: { alignSelf: 'center' },
  // Boîte de 320 pt, centrée dans l'enveloppe puis réduite autour de son centre.
  boite: { position: 'absolute', width: BOITE, height: BOITE },

  pointille: {
    position: 'absolute',
    left: 34,
    top: 34,
    right: 34,
    bottom: 34,
    borderRadius: 999,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.bordureNuit,
  },
  anneauInterieur: {
    position: 'absolute',
    left: 82,
    top: 82,
    right: 82,
    bottom: 82,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.surfaceNuit,
  },

  emplacement: { position: 'absolute', left: ORIGINE, top: ORIGINE, width: PUCE, height: PUCE },
  puce: {
    width: PUCE,
    height: PUCE,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 4,
    shadowColor: '#030A28',
    shadowOpacity: 0.35,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },
  libelle: { fontSize: 10.5, lineHeight: 12, fontWeight: '700', color: colors.night, textAlign: 'center' },

  coeur: { position: 'absolute', left: 95, top: 95, width: 130, height: 130, borderRadius: 65, padding: 3 },
  coeurInterieur: {
    flex: 1,
    borderRadius: 62,
    backgroundColor: '#0F2A66',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 8,
  },
  coeurLibelle: { fontSize: 14, fontWeight: '800', color: colors.white, textAlign: 'center' },
});
